// One week of `games`, fetched once for whoever asks first.
//
// The Scoreboard and Teams tabs both need the same rows for the same week:
// scores, kickoff times, spreads and status per team. They were issuing the
// same query with slightly different column lists, so switching between the
// two tabs refetched a table neither of them had changed, and only one of
// them cached the result.
//
// Keyed without the league, because a week's fixtures are the same for every
// league. A manager browsing back through the season in one league warms the
// cache for their others. That also means invalidateLeague does not sweep
// these keys, which is correct: no league action changes a game.
import { supabase } from "../supabase/supabase";
import { SEASON_YEAR } from "./season";
import {
  getCached,
  setCached,
  ttlForGames,
  markLive,
  LIVE_TTL_MS,
  STATIC_TTL_MS,
} from "./queryCache";

// Every column either caller needs, so one cached payload serves both. Adding
// a column here is cheap; keeping two lists in step was not.
export const WEEK_GAME_COLUMNS =
  "week, home_team, away_team, home_score, away_score, home_spread, period, clock, game_status, game_complete, date, start_time_tbd";

/**
 * Raw `games` rows for one week. Pass `isFinished` when the week is behind
 * the one being played: those rows cannot change again, so they are cached to
 * disk for a day rather than for the few minutes a live week gets.
 */
export async function fetchWeekGames(week, { isFinished = false } = {}) {
  const cacheKey = `games:${SEASON_YEAR}:week:${week}`;
  const cached = getCached(cacheKey);
  if (cached) return cached;

  const { data, error } = await supabase
    .from("games")
    .select(WEEK_GAME_COLUMNS)
    .eq("year", SEASON_YEAR)
    .eq("week", week);
  const rows = data || [];

  // A failed request and a week whose rows have not been imported yet both
  // arrive here as an empty list, and caching either one hides the real games
  // for the rest of the TTL. On the persisted path that is a day, and the only
  // way out is signing out. Return the empty list without caching it.
  if (error || !rows.length) return rows;

  if (isFinished) {
    setCached(cacheKey, rows, STATIC_TTL_MS, { persist: true });
  } else {
    // Two minutes while anything is in progress, matching the interval
    // backend/espn.js ingests on, and five when nothing is.
    const ttl = ttlForGames(rows);
    setCached(cacheKey, rows, ttl);
    if (ttl === LIVE_TTL_MS) markLive(cacheKey);
  }

  return rows;
}
